import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CaseDocumentation = ({ image, onCompare }) => {
  const [activeTab, setActiveTab] = useState('notes'); // notes, diagnosis, treatment
  const [newNote, setNewNote] = useState('');
  const [selectedDiagnosis, setSelectedDiagnosis] = useState('');
  const [selectedTreatment, setSelectedTreatment] = useState('');

  const caseNotes = [
    {
      id: 1,
      date: '2026-01-15',
      time: '09:45 AM',
      author: 'Dr. Chen',
      note: 'Moderate caries detected on tooth #3. Recommend composite filling.',
      linkedImage: true
    },
    {
      id: 2,
      date: '2026-01-12',
      time: '02:30 PM',
      author: 'Dr. Anderson',
      note: 'Periapical X-ray shows good bone density. No signs of infection.',
      linkedImage: true
    },
    {
      id: 3,
      date: '2026-01-10',
      time: '11:15 AM',
      author: 'Dr. Chen',
      note: 'CBCT scan completed for implant planning. Adequate bone volume present.',
      linkedImage: true
    }
  ];

  const diagnosisCodes = [
    { value: 'K02.51', label: 'K02.51 - Dental caries on pit and fissure surface limited to enamel' },
    { value: 'K02.52', label: 'K02.52 - Dental caries on pit and fissure surface penetrating into dentin' },
    { value: 'K04.7', label: 'K04.7 - Periapical abscess without sinus' },
    { value: 'K08.129', label: 'K08.129 - Complete loss of teeth due to trauma' },
    { value: 'K05.10', label: 'K05.10 - Chronic gingivitis, plaque induced' }
  ];

  const treatmentCodes = [
    { value: 'D2391', label: 'D2391 - Resin-based composite - one surface, posterior' },
    { value: 'D2392', label: 'D2392 - Resin-based composite - two surfaces, posterior' },
    { value: 'D3310', label: 'D3310 - Endodontic therapy, anterior tooth' },
    { value: 'D6010', label: 'D6010 - Surgical placement of implant body' },
    { value: 'D2740', label: 'D2740 - Crown - porcelain/ceramic substrate' }
  ];

  const handleAddNote = () => {
    if (newNote) {
      // Add note logic
      setNewNote('');
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2">
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={16} />
          Case Documentation
        </h3>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'notes' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </button>
        <button
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'diagnosis' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          onClick={() => setActiveTab('diagnosis')}
        >
          Diagnosis
        </button>
        <button
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'treatment' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          onClick={() => setActiveTab('treatment')}
        >
          Treatment
        </button>
      </div>
      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'notes' && (
          <div className="space-y-4">
            {/* Add Note */}
            <div className="space-y-2">
              <Input
                placeholder="Add clinical note..."
                value={newNote}
                onChange={(e) => setNewNote(e?.target?.value)}
              />
              <Button
                variant="default"
                size="sm"
                fullWidth
                iconName="Plus"
                onClick={handleAddNote}
                disabled={!newNote}
              >
                Add Note
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {caseNotes?.map((note) => (
                <div
                  key={note?.id}
                  className="p-3 bg-muted/50 rounded border border-border"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name="User" size={14} className="text-primary" />
                      <span className="text-xs font-medium text-foreground">
                        {note?.author}
                      </span>
                    </div>
                    {note?.linkedImage && (
                      <div className="flex items-center gap-1 text-xs text-primary">
                        <Icon name="Image" size={12} />
                        <span>Linked</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-foreground mb-2">{note?.note}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Calendar" size={12} />
                    <span>{note?.date}</span>
                    <Icon name="Clock" size={12} />
                    <span>{note?.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'diagnosis' && (
          <div className="space-y-4">
            <Select
              label="Add Diagnosis Code"
              placeholder="Search diagnosis codes..."
              value={selectedDiagnosis}
              onChange={setSelectedDiagnosis}
              options={diagnosisCodes}
              searchable
            />

            {/* Current Diagnoses */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Current Diagnoses
              </h4>
              <div className="space-y-2">
                <div className="p-3 bg-muted/50 rounded border border-border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground mb-1">
                        K02.52
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Dental caries on pit and fissure surface penetrating into dentin
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Icon name="MapPin" size={12} />
                        <span>Tooth #3</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="xs" iconName="Trash2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'treatment' && (
          <div className="space-y-4">
            <Select
              label="Add Treatment Code"
              placeholder="Search CDT codes..."
              value={selectedTreatment}
              onChange={setSelectedTreatment}
              options={treatmentCodes}
              searchable
            />

            {/* Treatment Plan */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Recommended Treatment
              </h4>
              <div className="space-y-2">
                <div className="p-3 bg-muted/50 rounded border border-border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground mb-1">
                        D2391
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Resin-based composite - one surface, posterior
                      </p>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Icon name="MapPin" size={12} />
                          <span>Tooth #3</span>
                        </div>
                        <div className="flex items-center gap-1 text-warning">
                          <Icon name="Clock" size={12} />
                          <span>Scheduled</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="xs" iconName="Edit" />
                  </div>
                </div>
              </div>
            </div>

            {/* Compare Button */}
            <Button
              variant="outline"
              size="sm"
              fullWidth
              iconName="Columns"
              onClick={onCompare}
            >
              Compare Before/After Images
            </Button>
          </div>
        )}
      </div>
      {/* Image Info */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Image" size={14} />
            <span>{image?.category}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>{image?.date}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseDocumentation;